import { ActionIcon, Badge, Box, Modal, Paper, Text, Tooltip } from "@mantine/core";
import { IMinUserDepricated, IProjectDeprecated } from "@mctypes/global.types";
import { IMCBIMAPI } from "@mctypes/mcbim.types";
import { IMcBimMongo } from "@mctypes/mcbimMono.types";
import { IconTrash } from "@tabler/icons-react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { AG_GRID_LOCALE_NOR } from "../../../../../agGridLocale";
import { FailedAlert, MCLoader } from "../../../../../components";
import { useAppSelector } from "../../../../../hooks";
import { useGetMcBimMongoFilesStatsQuery } from "../McBimMongoRTK";
import { useRemoveMcFileDataMutation } from "../McBimRTK";
import { isMcBimAdmin } from "../mcBimSlice";
import FileVersions from "./McBimFileVersions";

type McBimExtractedFileRow = IMcBimMongo.FileStats & {
  count?: number;
  isActiveInMCBimProject?: boolean;
};

const McBimMongoExtractedFiles: React.FC<{
  mcBimProject: IMCBIMAPI.Project;
  project: IProjectDeprecated;
  isActive?: boolean;
}> = ({ mcBimProject, project, isActive = true }) => {
  const user = useAppSelector((state) => state.globalReducer.user);
  const userIsMcBimAdmin = isMcBimAdmin(project.mcbim.admins as IMinUserDepricated[], user?._id);

  const gridRef = useRef<AgGridReact<McBimExtractedFileRow>>(null);
  const [nrOfDeletedDocuments, setNrOfDeletedDocuments] = useState<number>(null);
  const filesInMCBimProject = mcBimProject.ifcFiles;

  const [removeFiles, { isLoading: removeFilesLoading, error: removeFilesError }] = useRemoveMcFileDataMutation();
  const {
    data: filesInMCBimMongoFromApi = [],
    isFetching: filesLoading,
    error: filesError,
    refetch,
  } = useGetMcBimMongoFilesStatsQuery(
    { projectId: project._id },
    {
      skip: !isActive,
      refetchOnMountOrArgChange: true,
    },
  );

  const filesInMCBimMongo = useMemo<McBimExtractedFileRow[]>(
    () =>
      filesInMCBimMongoFromApi.map((file) => {
        const activeFile = filesInMCBimProject.find((activeFile) => {
          return file._id.fileName === activeFile.fileName && file._id.fileStorageId === activeFile.fileStorageId;
        });
        return {
          ...file,
          isActiveInMCBimProject: activeFile != null,
        };
      }),
    [filesInMCBimMongoFromApi, filesInMCBimProject],
  );

  const removeAllFileVersions = useCallback(
    async (fileStorageId: string, fileName: string) => {
      const shouldDelete = window.confirm(`Er du sikker på at du vil slette alle versjoner av ${fileName}?`);
      if (!shouldDelete) {
        return;
      }

      const resp = await removeFiles({ projectId: project._id, fileStorageId }).unwrap();
      setNrOfDeletedDocuments(resp.deletedDocuments);
      refetch();
    },
    [project._id, refetch, removeFiles],
  );

  const columnDefs = useMemo<ColDef<McBimExtractedFileRow>[]>(
    () => [
      {
        field: "_id.fileName",
        headerName: "Filnavn",
        cellRenderer: "agGroupCellRenderer",
        filter: true,
        flex: 2,
        minWidth: 260,
        sortable: true,
      },
      {
        headerName: "Antall BIM-objekter",
        valueGetter: ({ data }) => data?.nrOfBimObjects ?? data?.count ?? 0,
        filter: "agNumberColumnFilter",
        flex: 1,
        minWidth: 180,
        sortable: true,
      },
      {
        headerName: "Aktiv",
        valueGetter: ({ data }) => (data?.isActiveInMCBimProject === true ? "Aktiv" : "Ikke aktiv"),
        cellRenderer: ({ data }: ICellRendererParams<McBimExtractedFileRow>) => (
          <Badge color={data?.isActiveInMCBimProject ? "green" : "red"} variant="light">
            {data?.isActiveInMCBimProject ? "Aktiv" : "Ikke aktiv"}
          </Badge>
        ),
        filter: true,
        width: 140,
        sortable: true,
      },
      {
        headerName: "",
        cellRenderer: ({ data }: ICellRendererParams<McBimExtractedFileRow>) => {
          if (data == null) return null;

          return (
            <Tooltip label="Slett alle versjoner" withArrow>
              <ActionIcon
                color="red"
                variant="subtle"
                disabled={userIsMcBimAdmin === false}
                onClick={() => removeAllFileVersions(data._id.fileStorageId, data._id.fileName)}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          );
        },
        width: 72,
        sortable: false,
        filter: false,
      },
    ],
    [removeAllFileVersions, userIsMcBimAdmin],
  );

  const detailCellRenderer = useCallback(
    ({ data }: ICellRendererParams<McBimExtractedFileRow>) => {
      if (data == null) return null;

      return (
        <Box p="sm" h="100%">
          <FileVersions project={project} file={data._id} />
        </Box>
      );
    },
    [project],
  );

  return (
    <Box h="100%" w="100%" p="sm" style={{ overflow: "auto" }}>
      {(removeFilesLoading === true || filesLoading === true) && <MCLoader loading />}
      {(removeFilesError != null || filesError != null) && <FailedAlert />}
      <InfoDialog deletedDocuments={nrOfDeletedDocuments} toggle={() => setNrOfDeletedDocuments(null)} />

      {filesInMCBimMongo?.length > 0 && (
        <Paper withBorder radius="md" h="100%" mih={360} style={{ overflow: "hidden" }}>
          <Box className="ag-theme-alpine" h="100%" mih={360}>
            <AgGridReact<McBimExtractedFileRow>
              ref={gridRef}
              rowData={filesInMCBimMongo}
              columnDefs={columnDefs}
              defaultColDef={{ resizable: true }}
              getRowId={({ data }) => data._id.fileStorageId}
              masterDetail
              detailCellRenderer={detailCellRenderer}
              detailRowHeight={245}
              animateRows
              localeText={AG_GRID_LOCALE_NOR}
            />
          </Box>
        </Paper>
      )}
      {filesInMCBimMongo.length === 0 && removeFilesLoading === false && filesLoading === false && (
        <Text c="dimmed">Ingen filer</Text>
      )}
    </Box>
  );
};

export default McBimMongoExtractedFiles;

export const InfoDialog: React.FC<{ deletedDocuments: number | null; toggle: () => void }> = ({
  deletedDocuments,
  toggle,
}) => {
  return (
    <Modal opened={deletedDocuments != null} onClose={toggle} title="Slettet">
      <Text>{deletedDocuments} dokumenter ble slettet</Text>
    </Modal>
  );
};
